import {
  DEFAULT_FEATURE_CFG,
  PALM_INDICES,
  TIP_INDICES,
  type FeatureExtractConfig,
  type GestureFeatures,
  type HandLandmarks,
  type Landmark,
} from './types'

function dist(a: Landmark, b: Landmark): number {
  const dx = a.x - b.x
  const dy = a.y - b.y
  return Math.hypot(dx, dy)
}

function maybeMirror(hand: HandLandmarks, mirrorX: boolean): HandLandmarks {
  if (!mirrorX) return hand
  return hand.map((p) => ({ x: 1 - p.x, y: p.y, z: p.z }))
}

export function palmCenter(hand: HandLandmarks): Landmark {
  let x = 0
  let y = 0
  let z = 0
  for (const i of PALM_INDICES) {
    const p = hand[i]
    x += p.x
    y += p.y
    z += p.z
  }
  const n = PALM_INDICES.length
  return { x: x / n, y: y / n, z: z / n }
}

/**
 * openness = mean tip-to-palm distance / wrist-to-middle-MCP scale, clamped 0..1
 */
export function computeOpenness(hand: HandLandmarks): number {
  if (hand.length < 21) return 0
  const palm = palmCenter(hand)
  const wrist = hand[0]
  const middleMcp = hand[9]
  const refScale = Math.max(dist(wrist, middleMcp), 1e-4)
  let sum = 0
  for (const i of TIP_INDICES) {
    sum += dist(hand[i], palm)
  }
  const mean = sum / TIP_INDICES.length
  // Empirically fist ~0.4 * ref, open ~1.6 * ref
  const raw = (mean / refScale - 0.35) / 1.2
  return Math.min(1, Math.max(0, raw))
}

export function computePushPull(
  hand: HandLandmarks,
  baselineScale: number,
  gain: number,
): number {
  if (hand.length < 21) return 0
  const wrist = hand[0]
  const middleMcp = hand[9]
  const scale = dist(wrist, middleMcp)
  const base = Math.max(baselineScale, 1e-4)
  const scaleDelta = (scale - base) / base
  // MediaPipe z: more negative ≈ closer to camera for some models; blend with scale
  const z = palmCenter(hand).z
  const mixed = scaleDelta * 0.7 + -z * 0.3
  return Math.min(1, Math.max(-1, mixed * gain))
}

function handScale(hand: HandLandmarks): number {
  return dist(hand[0], hand[9])
}

export function extractFeatures(
  hands: HandLandmarks[],
  handedness: Array<'Left' | 'Right'>,
  confidences: number[],
  cfg: FeatureExtractConfig = DEFAULT_FEATURE_CFG,
): GestureFeatures {
  const empty: GestureFeatures = {
    palmX: 0.5,
    palmY: 0.5,
    openness: 0,
    bothRaised: false,
    pushPull: 0,
    confidence: 0,
    handedness: 'None',
  }

  if (!hands.length) return empty

  const mirrored = hands.map((h) => maybeMirror(h, cfg.mirrorX))

  let primaryIdx = 0
  if (cfg.primaryHandPolicy === 'dominant') {
    let best = -1
    for (let i = 0; i < mirrored.length; i++) {
      const c = confidences[i] ?? 0
      if (c > best) {
        best = c
        primaryIdx = i
      }
    }
  } else if (cfg.primaryHandPolicy === 'firstDetected') {
    primaryIdx = 0
  }

  let palmX: number
  let palmY: number
  let openness: number
  let pushPull: number
  let confidence: number
  let handLabel: 'Left' | 'Right' | 'None'

  if (cfg.primaryHandPolicy === 'average' && mirrored.length >= 2) {
    const c0 = palmCenter(mirrored[0])
    const c1 = palmCenter(mirrored[1])
    palmX = (c0.x + c1.x) / 2
    palmY = (c0.y + c1.y) / 2
    openness = (computeOpenness(mirrored[0]) + computeOpenness(mirrored[1])) / 2
    const base = (handScale(mirrored[0]) + handScale(mirrored[1])) / 2
    pushPull =
      (computePushPull(mirrored[0], base, cfg.pushPullGain) +
        computePushPull(mirrored[1], base, cfg.pushPullGain)) /
      2
    confidence = ((confidences[0] ?? 0) + (confidences[1] ?? 0)) / 2
    handLabel = 'None'
  } else {
    const hand = mirrored[primaryIdx]
    const c = palmCenter(hand)
    palmX = c.x
    palmY = c.y
    openness = computeOpenness(hand)
    pushPull = computePushPull(hand, handScale(hand), cfg.pushPullGain)
    confidence = confidences[primaryIdx] ?? 0
    handLabel = handedness[primaryIdx] ?? 'None'
  }

  let bothRaised = false
  if (mirrored.length >= 2) {
    bothRaised = mirrored.every((h) => palmCenter(h).y < cfg.raiseY)
  }

  return {
    palmX,
    palmY,
    openness,
    bothRaised,
    pushPull,
    confidence,
    handedness: handLabel,
  }
}
